import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { Person, FlightTakeoff, AdminPanelSettings, Business } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useUnifiedAuth } from '../../contexts/UnifiedAuthContext';

interface ServiceUserIndicatorProps {
  showEmail?: boolean;
  variant?: 'compact' | 'full';
}

const ServiceUserIndicator: React.FC<ServiceUserIndicatorProps> = ({ 
  showEmail = true, 
  variant = 'compact' 
}) => {
  const { t } = useTranslation();
  const auth = useUnifiedAuth();
  
  if (!auth.isAuthenticated) {
    return null;
  }
  
  const getIcon = () => {
    switch (auth.userType) {
      case 'platform_admin':
        return <AdminPanelSettings />;
      case 'club_manager':
        return <Business />;
      case 'pilot':
        return <FlightTakeoff />;
      case 'guest':
      default:
        return <Person />;
    }
  };
  
  const getLabel = () => {
    switch (auth.userType) {
      case 'platform_admin':
        return t('userType.platformAdmin');
      case 'club_manager':
        return t('userType.clubManager');
      case 'pilot':
        return t('userType.pilot');
      case 'guest':
        return t('userType.serviceGuest');
      default:
        return t('userType.user');
    }
  };
  
  const getColor = () => {
    switch (auth.userType) {
      case 'platform_admin':
        return 'error' as const;
      case 'club_manager':
        return 'warning' as const;
      case 'pilot':
        return 'primary' as const;
      case 'guest':
      default:
        return 'default' as const;
    }
  };
  
  if (variant === 'full') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={getIcon()}
            label={getLabel()}
            color={getColor()}
            size="small"
            variant={auth.userType === 'guest' ? 'outlined' : 'filled'}
          />
          {auth.serviceUser?.has_premium_access && auth.userType === 'guest' && (
            <Chip
              label="Premium"
              color="secondary"
              size="small"
              variant="filled"
            />
          )}
        </Box>
        {showEmail && (
          <Typography variant="caption" color="text.secondary">
            {auth.email}
          </Typography>
        )}
      </Box>
    );
  }
  
  // Compact variant
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Chip
        icon={getIcon()}
        label={getLabel()}
        color={getColor()}
        size="small"
        variant={auth.userType === 'guest' ? 'outlined' : 'filled'}
      />
      {showEmail && (
        <Typography variant="caption" color="text.secondary">
          {auth.displayName || auth.email}
        </Typography>
      )}
    </Box>
  );
};

export default ServiceUserIndicator;